import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SKIP_AUTH, SKIP_GLOBAL_ERROR_TOAST } from '../../interceptors/token/token.interceptor';
import { sessionAuthData } from '../../interfaces/states-data';
import { getSessionAuthData } from '../../states/session-auth/session-auth.selectors';
import { MAINTENANCE_ROUTE, shouldShowMaintenance } from '../../utils/maintenance-flow.util';

/** Lo que responde `GET configuration/maintenance/status` (público). */
export interface MaintenanceStatus {
  active: boolean;
  started_at: string | null;
  message: string | null;
}

/** Una fila del historial (`configuration.maintenance_window`). */
export interface MaintenanceWindowRow {
  id: number | string;
  started_at: string;
  started_by_email: string;
  message: string | null;
  ended_at: string | null;
  ended_by_email: string | null;
}

/** Una persona con sesión activa. Negocio = perfil propio (super admin, KEY ACCOUNT…) o asignada a una experiencia. */
export interface MaintenanceActivePerson {
  user_id: string;
  nombre: string;
  email: string;
  tipo: 'negocio' | 'cliente';
  /** Perfiles de la cuenta sin el base «Productor», que tiene todo registro. */
  perfiles: string[];
  /** Perfiles con los que está asignada en experiencias (Productor, Taquillero…). */
  perfiles_en_experiencias: string[];
  experiencias: number;
  ultima_actividad: string;
}

export interface MaintenanceActivity {
  holds_vigentes: number;
  cajas_abiertas: number;
  pagos_pendientes_15min: number;
  sesiones_activas_15min: number;
  /** Quiénes son, hasta 5. Opcional: un core anterior no lo manda. */
  personas_activas?: MaintenanceActivePerson[];
  consultado_en: string;
}

const INACTIVE: MaintenanceStatus = { active: false, started_at: null, message: null };

/** Cada cuánto revisa la app, abierta, si se activó un mantenimiento. */
export const MAINTENANCE_WATCH_INTERVAL_MS = 60_000;
/** Cuánto vale el último estado para el guard de navegación antes de volver a preguntar. */
export const MAINTENANCE_STATUS_MAX_AGE_MS = 15_000;

/**
 * Estado del modo mantenimiento del lado del navegador.
 *
 * - `getStatus` pregunta al servidor y recuerda la respuesta un rato, así el guard de cada
 *   navegación no dispara una petición por clic. Si la red falla, devuelve lo último que
 *   supo (o «inactivo»): un error de red nunca manda a nadie a la pantalla de
 *   mantenimiento ni saca de ella a quien ya está.
 * - `startWatching` revisa cada minuto mientras la app está abierta: a quien ya estaba
 *   adentro cuando se activa lo manda a la pantalla, salvo que tenga una compra en curso
 *   (ver `maintenance-flow.util.ts`).
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly store = inject<Store<{ sessionAuthState: sessionAuthData }>>(Store);
  private readonly zone = inject(NgZone);
  private readonly base = `${environment.apiBase}configuration/maintenance`;

  private readonly statusSubject = new BehaviorSubject<MaintenanceStatus | null>(null);
  readonly status$: Observable<MaintenanceStatus | null> = this.statusSubject.asObservable();
  private fetchedAt = 0;
  private pending: Promise<MaintenanceStatus> | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  get lastKnown(): MaintenanceStatus | null {
    return this.statusSubject.value;
  }

  getStatus(maxAgeMs: number = MAINTENANCE_STATUS_MAX_AGE_MS): Promise<MaintenanceStatus> {
    const cached = this.statusSubject.value;
    if (cached && Date.now() - this.fetchedAt < maxAgeMs) {
      return Promise.resolve(cached);
    }
    if (this.pending) {
      return this.pending;
    }
    // SKIP_AUTH: es pública y la llama todo visitante; sin ella, un 401 del servidor
    // pasaría por «sesión expirada» y echaría al login a quien iba a comprar.
    this.pending = firstValueFrom(
      this.http.get<MaintenanceStatus>(`${this.base}/status`, {
        context: new HttpContext().set(SKIP_GLOBAL_ERROR_TOAST, true).set(SKIP_AUTH, true),
      }),
    )
      .then((raw) => {
        const status = normalizeStatus(raw);
        this.statusSubject.next(status);
        this.fetchedAt = Date.now();
        return status;
      })
      .catch(() => cached ?? INACTIVE)
      .finally(() => {
        this.pending = null;
      });
    return this.pending;
  }

  /** ¿Esta navegación debe ir a la pantalla de mantenimiento? */
  async shouldRedirect(url: string, maxAgeMs?: number): Promise<boolean> {
    const status = await this.getStatus(maxAgeMs);
    if (!status.active) {
      return false;
    }
    const auth = await firstValueFrom(this.store.pipe(select(getSessionAuthData), take(1)));
    return shouldShowMaintenance({
      active: true,
      isSuperAdmin: !!auth?.is_super_admin,
      url,
      storage: safeLocalStorage(),
    });
  }

  /** La pantalla, con la URL a la que volver cuando termine. */
  maintenanceUrlTree(volver: string): UrlTree {
    return this.router.createUrlTree([MAINTENANCE_ROUTE], { queryParams: { volver } });
  }

  startWatching(): void {
    if (this.timer) {
      return;
    }
    // Fuera de la zona: un intervalo adentro mantendría a Angular «ocupado» todo el tiempo
    // (y rompería los tests que esperan estabilidad). La navegación sí vuelve a la zona.
    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(() => void this.checkCurrentRoute(), MAINTENANCE_WATCH_INTERVAL_MS);
    });
  }

  stopWatching(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  async checkCurrentRoute(): Promise<void> {
    const url = this.router.url;
    if (await this.shouldRedirect(url, 0)) {
      this.zone.run(() => void this.router.navigateByUrl(this.maintenanceUrlTree(url)));
    }
  }

  history(limit = 50): Observable<MaintenanceWindowRow[]> {
    return this.http.get<MaintenanceWindowRow[]>(`${this.base}/history`, { params: { limit } });
  }

  activity(): Observable<MaintenanceActivity> {
    return this.http.get<MaintenanceActivity>(`${this.base}/activity`);
  }

  activate(message: string | null): Observable<MaintenanceWindowRow> {
    const body = message && message.trim() ? { message: message.trim() } : {};
    return this.http.post<MaintenanceWindowRow>(`${this.base}/activate`, body).pipe(tap(() => this.invalidate()));
  }

  deactivate(): Observable<MaintenanceWindowRow> {
    return this.http.post<MaintenanceWindowRow>(`${this.base}/deactivate`, {}).pipe(tap(() => this.invalidate()));
  }

  /** Olvida el último estado: la próxima consulta va al servidor. */
  invalidate(): void {
    this.fetchedAt = 0;
  }
}

function normalizeStatus(raw: Partial<MaintenanceStatus> | null | undefined): MaintenanceStatus {
  return {
    active: !!raw?.active,
    started_at: raw?.started_at ?? null,
    message: raw?.message ?? null,
  };
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
